/**
 * Phase BACKGROUND-ANALYSIS-P1a.5-vi — stuck-pending detection.
 *
 * Returns raw `isStuck` signal once outfit age > threshold for its
 * input_method. Hook itself does NOT apply the 250ms minimum-visible
 * guard — that lives in the consumer (strip mount logic) so the guard
 * resets cleanly if the outfit completes mid-flight.
 *
 * Polls every 5s + on visibilitychange (covers tab-backgrounded case
 * where setTimeout is throttled and a 2min threshold may elapse before
 * the next render tick).
 */
import { useEffect, useRef, useState } from "react";
import { trackEvent } from "@/lib/analytics";

const STUCK_THRESHOLDS_MS: Record<string, number> = {
  photo: 120_000,
  screenshot: 120_000,
  share_intent: 75_000,
  url: 60_000,
  text: 40_000,
  social: 75_000,
};

export interface StuckDetectionInput {
  id: string;
  created_at: string;
  input_method: string | null;
  analysis_status: string | null;
}

export function useStuckDetection(outfit: StuckDetectionInput | null): boolean {
  const [isStuck, setIsStuck] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    if (!outfit) {
      setIsStuck(false);
      return;
    }
    if (
      outfit.analysis_status === "complete" ||
      outfit.analysis_status === "failed"
    ) {
      setIsStuck(false);
      return;
    }

    const threshold =
      STUCK_THRESHOLDS_MS[outfit.input_method ?? "photo"] ?? 120_000;

    const check = () => {
      const elapsed = Date.now() - new Date(outfit.created_at).getTime();
      const stuck = elapsed > threshold;
      if (stuck && !firedRef.current) {
        firedRef.current = true;
        trackEvent("outfit_stuck_detected", {
          outfit_id: outfit.id,
          input_method: outfit.input_method,
          age_seconds: Math.floor(elapsed / 1000),
          threshold_seconds: Math.floor(threshold / 1000),
          analysis_status: outfit.analysis_status,
        });
      }
      setIsStuck(stuck);
    };

    check();
    const id = window.setInterval(check, 5_000);
    const onVis = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [
    outfit?.id,
    outfit?.created_at,
    outfit?.input_method,
    outfit?.analysis_status,
  ]);

  return isStuck;
}
